import React from 'react';
import Serializer, {
  type SerializeArguments,
  type DeserializeArguments,
} from './Serializer';
import { type LinkCardElement } from '../../types/aha-editor';
import { type Descendant } from 'slate';
import { isHTMLElementNode } from '../helpers/serializerHelpers';
import { isLinkCardElement } from '../../types/guards';

class SerializerLinkCard extends Serializer {
  static deserialize({
    el,
    next,
    markAttributes,
  }: DeserializeArguments): LinkCardElement | Descendant[] | undefined {
    if (!isHTMLElementNode(el) || !el.dataset.linkCard) return;

    try {
      const data = JSON.parse(el.dataset.linkCard) as Partial<LinkCardElement>;
      const {
        linkCard_href,
        linkCard_title,
        linkCard_description,
        linkCard_linkText,
        linkCard_image,
        linkCard_fullWidth,
        linkCard_keepRatio,
        linkCard_orientation,
        linkCard_alignment,
        resizer_width,
      } = data;

      return {
        type: 'linkCard',
        linkCard_href,
        linkCard_title,
        linkCard_description,
        linkCard_linkText,
        linkCard_image,
        linkCard_fullWidth,
        linkCard_keepRatio,
        linkCard_orientation,
        linkCard_alignment,
        resizer_width,
        children: [{ text: '' }],
      };
    } catch (e) {
      console.error('Error deserializing link card:', e);
    }
  }

  static serialize({ node }: SerializeArguments): React.ReactNode {
    if (!isLinkCardElement(node)) return;

    const {
      linkCard_href,
      linkCard_title,
      linkCard_description,
      linkCard_linkText,
      linkCard_image,
      linkCard_fullWidth,
      linkCard_orientation,
      linkCard_alignment,
      resizer_width,
    } = node;

    return (
      <a
        className="linkCard"
        href={linkCard_href}
        target="_blank"
        rel="noopener noreferrer"
        data-link-card={JSON.stringify(node)}
        style={{
          display: linkCard_fullWidth ? 'flex' : 'inline-flex',
          width: resizer_width || undefined,
          flexDirection:
            linkCard_orientation === 'horizontal' ? 'row' : 'column',
          textAlign: linkCard_alignment || 'left',
        }}
      >
        {linkCard_image && (
          <img src={linkCard_image} alt={linkCard_title || 'Link preview'} />
        )}
        {linkCard_title && (
          <span className="linkCard__title">{linkCard_title}</span>
        )}
        {linkCard_description && (
          <span className="linkCard__description">{linkCard_description}</span>
        )}
        {linkCard_href && (
          <span className="linkCard__url">
            {linkCard_linkText || linkCard_href}{' '}
            <i className={'fa-regular fa-arrow-right'} />
          </span>
        )}
      </a>
    );
  }
}

export default SerializerLinkCard;
